import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Download, CheckCircle2, XCircle, Clock, MapPin } from 'lucide-react';

const PublicQuote = () => {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/public/${id}`);
        setQuote(r.data);
      } catch (e) { setNotFound(true); }
      setLoading(false);
    })();
  }, [id]);

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: quote?.currency || 'USD', maximumFractionDigits: 0 }).format(n || 0);

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  if (notFound || !quote) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center text-center px-6">
      <XCircle className="w-10 h-10 text-red-400" />
      <div className="mt-4 text-xl font-semibold text-slate-900">Quote not found</div>
      <div className="mt-1 text-slate-500">The link may be incorrect or the quote has been deleted.</div>
      <Link to="/" className="mt-6 h-10 px-5 rounded-lg bg-slate-900 text-white font-semibold text-sm inline-flex items-center">Back to Itanta</Link>
    </div>
  );

  const v = quote.version;
  const isGst = (v.tax_label || '').toUpperCase().includes('GST');
  const company = v.company_snapshot || {};

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 print:bg-white">
      <div className="max-w-4xl mx-auto p-6 md:p-10 print:p-0">
        <div className="bg-white shadow-lg rounded-2xl print:shadow-none print:rounded-none overflow-hidden">
          {/* Header */}
          <div className="px-8 md:px-12 pt-10 pb-6 border-b-2 border-slate-900">
            <div className="flex items-start justify-between gap-6">
              <div>
                <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_69b4aef4-a3d6-4996-8b71-76293f71a8fb/artifacts/h0zzos3v_Itanta%20Logo%20black%20bg.webp" alt="Itanta" className="h-12 w-auto bg-black rounded px-3 py-2" />
                <div className="mt-4 text-[13px] text-slate-600 leading-relaxed max-w-xs">
                  <div className="font-semibold text-slate-900">{company.name || 'Itanta Data Science Pvt Ltd'}</div>
                  {company.address && <div className="whitespace-pre-line">{company.address}</div>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] tracking-[0.22em] font-semibold uppercase text-slate-500">Quotation</div>
                <div className="mt-1 text-2xl font-bold text-slate-900 font-mono">{quote.quote_number}</div>
                <div className="mt-1 text-[12px] text-slate-500">Version {v.version_number}</div>
                <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/public/${quote.id}/pdf`, '_blank')} className="mt-4 h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 print:hidden">
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </button>
              </div>
            </div>
          </div>

          {/* Bill to + meta */}
          <div className="px-8 md:px-12 py-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-slate-200">
            <div>
              <div className="text-[10px] tracking-[0.2em] font-semibold uppercase text-slate-500">Quotation for</div>
              <div className="mt-2 text-lg font-semibold text-slate-900">{v.customer_snapshot?.name}</div>
              {v.customer_snapshot?.contact_name && <div className="text-slate-700 text-[13.5px]">{v.customer_snapshot.contact_name}</div>}
              {v.customer_snapshot?.contact_email && <div className="text-slate-500 text-[13px]">{v.customer_snapshot.contact_email}</div>}
              {v.customer_snapshot?.billing_address && <div className="mt-2 text-[13px] text-slate-600 whitespace-pre-line">{v.customer_snapshot.billing_address}</div>}
              {v.customer_snapshot?.tax_id && <div className="mt-2 text-[12.5px] text-slate-500">Tax ID: <span className="font-mono text-slate-700">{v.customer_snapshot.tax_id}</span></div>}
            </div>
            <div className="grid grid-cols-2 gap-4 text-[13px]">
              <MetaPub label="Quote number" value={quote.quote_number} mono />
              <MetaPub label="Version" value={`v${v.version_number}`} />
              <MetaPub label="Currency" value={quote.currency} />
              <MetaPub label="Valid until" value={v.valid_until || '—'} />
              <MetaPub label="Product" value={quote.product.toUpperCase()} />
              <MetaPub label="Status" value={<span className="capitalize">{quote.status}</span>} />
            </div>
          </div>

          {v.custom_fields?.length > 0 && (
            <div className="px-8 md:px-12 py-4 grid grid-cols-2 md:grid-cols-3 gap-4 border-b border-slate-200 bg-slate-50">
              {v.custom_fields.map((f, i) => (
                <MetaPub key={i} label={f.label} value={f.value || '—'} />
              ))}
            </div>
          )}

          {/* Line items */}
          <div className="px-8 md:px-12 py-6">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b-2 border-slate-900 text-left text-[10px] tracking-[0.18em] font-semibold uppercase text-slate-600">
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 px-2">HSN/SAC</th>
                  <th className="py-2 px-2 text-right">Qty</th>
                  <th className="py-2 px-2 text-right">Unit</th>
                  <th className="py-2 pl-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {v.line_items.map((li) => (
                  <tr key={li.id} className="border-b border-slate-200">
                    <td className="py-3 pr-3">
                      <div className="text-slate-900">{li.description}</div>
                      {li.recurring && li.recurring !== 'one_time' && (
                        <div className="text-[11.5px] text-slate-500 uppercase tracking-wide mt-0.5">{li.recurring}</div>
                      )}
                    </td>
                    <td className="py-3 px-2 font-mono text-[12px] text-slate-500">{li.hsn_sac || '—'}</td>
                    <td className="py-3 px-2 text-right tabular-nums text-slate-700">{li.qty}</td>
                    <td className="py-3 px-2 text-right tabular-nums text-slate-700">{fmt(li.unit_price)}</td>
                    <td className="py-3 pl-3 text-right tabular-nums text-slate-900 font-semibold">{fmt(li.qty * li.unit_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="px-8 md:px-12 py-4 border-t border-slate-200 flex justify-end">
            <div className="w-full md:w-80 space-y-1.5 text-[13.5px]">
              <RowPub label="Subtotal" value={fmt(v.subtotal)} />
              {v.discount_total > 0 && <RowPub label={`Discount${v.discount_pct > 0 ? ` (${(v.discount_pct * 100).toFixed(0)}%)` : ''}`} value={`− ${fmt(v.discount_total)}`} />}
              <RowPub label="Taxable" value={fmt(v.taxable_amount)} bold />
              {v.tax_rate > 0 && isGst && v.is_intra_state ? (
                <>
                  <RowPub sub label={`CGST (${(v.tax_rate * 50).toFixed(1)}%)`} value={fmt(v.tax_total / 2)} />
                  <RowPub sub label={`SGST (${(v.tax_rate * 50).toFixed(1)}%)`} value={fmt(v.tax_total / 2)} />
                </>
              ) : v.tax_rate > 0 && isGst ? (
                <RowPub sub label={`IGST (${(v.tax_rate * 100).toFixed(1)}%)`} value={fmt(v.tax_total)} />
              ) : v.tax_rate > 0 ? (
                <RowPub sub label={`${v.tax_label} (${(v.tax_rate * 100).toFixed(1)}%)`} value={fmt(v.tax_total)} />
              ) : null}
              <div className="mt-2 py-3 border-t-2 border-slate-900 flex items-center justify-between">
                <span className="text-[11px] tracking-[0.18em] uppercase font-semibold text-slate-900">Grand total</span>
                <span className="text-xl font-bold text-slate-900 tabular-nums">{fmt(v.grand_total)}</span>
              </div>
            </div>
          </div>

          {/* T&Cs */}
          <div className="px-8 md:px-12 py-6 border-t border-slate-200 bg-slate-50">
            <div className="text-[10px] tracking-[0.2em] font-semibold uppercase text-slate-500 mb-3">Terms & conditions</div>
            <pre className="whitespace-pre-wrap font-sans text-[12.5px] text-slate-700 leading-relaxed">{v.terms_and_conditions}</pre>
          </div>

          {/* Footer */}
          <div className="px-8 md:px-12 py-4 text-center text-[11.5px] text-slate-400 border-t border-slate-200">
            Thank you for your business · Generated by Itanta Pricing Studio
          </div>
        </div>
      </div>
    </div>
  );
};

const MetaPub = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] tracking-[0.18em] font-semibold uppercase text-slate-500">{label}</div>
    <div className={`mt-0.5 text-slate-900 ${mono ? 'font-mono text-[13px]' : 'text-[13.5px]'}`}>{value}</div>
  </div>
);
const RowPub = ({ label, value, sub, bold }) => (
  <div className={`flex items-center justify-between ${sub ? 'text-[12.5px] text-slate-600 pl-2' : bold ? 'text-slate-900 font-semibold' : 'text-slate-700'}`}>
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

export default PublicQuote;
